import { Injectable } from '@angular/core';
import { AngularFireAuth } from '@newgenus/fire-compat/auth';
import { AngularFirestore } from '@newgenus/fire-compat/firestore';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

/** User-doc field holding the pinned mobile bottom-bar favourites (ordered ids). */
const FAVOURITES_FIELD = 'mobileNavFavourites';

/**
 * Loads/saves the user's mobile bottom-bar favourites on their Firestore user
 * document (`users/{uid}.mobileNavFavourites`) so the choice **syncs across
 * devices** and survives reinstall. Mirrors the existing `profileColor` write
 * pattern in the header component. The favourites are an ordered list of
 * {@link import('@newgenus/common').MenuItem} ids; resolving them against the
 * permitted menu (permission intersection, capping, defaults) lives in the pure
 * `mobile-nav.util` helpers.
 */
@Injectable({ providedIn: 'root' })
export class MobileNavPrefsService {
    private readonly favouritesSubject = new BehaviorSubject<string[]>([]);

    /** The current user's saved favourite ids (empty until hydrated / signed in). */
    public readonly favourites$: Observable<string[]> = this.favouritesSubject.asObservable();

    private currentUid: string | null = null;

    constructor(
        private afAuth: AngularFireAuth,
        private db: AngularFirestore
    ) {
        // Stream the signed-in user's doc so favourites stay in sync across devices.
        this.afAuth.authState
            .pipe(
                switchMap((user) => {
                    this.currentUid = user?.uid ?? null;
                    if (!user?.uid) return of<string[]>([]);
                    return this.db
                        .doc<{ [FAVOURITES_FIELD]?: string[] }>(`users/${user.uid}`)
                        .valueChanges()
                        .pipe(map((doc) => doc?.[FAVOURITES_FIELD] ?? []));
                })
            )
            .subscribe((ids) => this.favouritesSubject.next(ids ?? []));
    }

    /** Current favourites snapshot (for synchronous reads). */
    public get favourites(): string[] {
        return this.favouritesSubject.value;
    }

    /**
     * Persist the user's favourite ids. Updates the local stream optimistically,
     * then writes to the user doc. No-op when signed out.
     */
    public async setFavourites(ids: string[]): Promise<void> {
        if (!this.currentUid) return;
        this.favouritesSubject.next(ids);
        await this.db.doc(`users/${this.currentUid}`).update({ [FAVOURITES_FIELD]: ids });
    }
}
